import { Table, Text } from '@mantine/core'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { CrossCameraPipeline } from '@/types/crossCamera'
import { CrossCameraCameraList } from '../CrossCameraCameraList/CrossCameraCameraList'
import { CrossCameraPipelineListActions } from '../CrossCameraPipelineListActions/CrossCameraPipelineListActions'
import { CrossCameraPipelineStatusBadge } from '../CrossCameraPipelineStatusBadge/CrossCameraPipelineStatusBadge'

type CrossCameraPipelineListProps = {
  pipelines: CrossCameraPipeline[]
  isLoading?: boolean
  hasError?: boolean
  errorMessage?: ReactNode
  onReload?: () => void
  onEdit: (pipeline: CrossCameraPipeline) => void
  onDelete: (pipeline: CrossCameraPipeline) => void
  onRestart: (pipeline: CrossCameraPipeline) => void
}

export const CrossCameraPipelineList = ({
  pipelines,
  isLoading,
  hasError,
  errorMessage,
  onReload,
  onEdit,
  onDelete,
  onRestart
}: CrossCameraPipelineListProps) => {
  return (
    <DataTable
      hasItems={pipelines.length > 0}
      isLoading={isLoading}
      hasError={hasError}
      emptyMessage={<FormattedMessage id="crossCamera.pipelines.empty" />}
      errorMessage={
        errorMessage ?? <FormattedMessage id="crossCamera.pipelines.error" />
      }
      onReload={onReload}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="crossCamera.pipelines.fields.name" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="crossCamera.pipelines.fields.application" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="crossCamera.pipelines.fields.cameras" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="crossCamera.pipelines.fields.model" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="crossCamera.pipelines.fields.status" />
          </Table.Th>
          <Table.Th ta="right">
            <FormattedMessage id="crossCamera.pipelines.fields.actions" />
          </Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {pipelines.map((pipeline) => (
          <Table.Tr key={pipeline.id}>
            <Table.Td fw={600}>{pipeline.name}</Table.Td>

            <Table.Td>
              <Text size="sm">{pipeline.application.name}</Text>
            </Table.Td>

            <Table.Td>
              <CrossCameraCameraList cameras={pipeline.cameras} />
            </Table.Td>

            <Table.Td>
              <Text size="sm">
                <span>{pipeline.model?.name}</span>
              </Text>
            </Table.Td>

            <Table.Td>
              <CrossCameraPipelineStatusBadge
                status={pipeline.status}
                errorMessage={pipeline.error_message}
              />
            </Table.Td>

            <Table.Td>
              <CrossCameraPipelineListActions
                pipeline={pipeline}
                onEdit={onEdit}
                onDelete={onDelete}
                onRestart={onRestart}
              />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </DataTable>
  )
}
